import React from "react";
import {
  programPaymentScheduleStudentAcademy,
  programsStudentAcademy,
} from "../../../../shared/services";
import { ProgramStudentType } from "../../../../types/dto/program";
import { CopyProgramSchedulePaymentStudentType } from "../../../../types/dto/scheduleStudent";
import { PaginatedResponse } from "../../../../types/dto/program";
import { calculateScheduleQuote } from "../../../academyProgramPersonalInfo/utils/paymentQuoteUtils";

export type TeacherPaymentStatus = "paid" | "unpaid";

export interface StudentPaymentSummaryRow extends ProgramStudentType {
  paymentStatus: TeacherPaymentStatus;
}

/** Misma lógica que el join del API y la vista admin (balance + estado). */
function resolveSchedulePaymentStatus(
  schedule: CopyProgramSchedulePaymentStudentType
): string {
  const quote = calculateScheduleQuote(schedule);
  const balance = Math.max(0, Number(schedule.balance ?? quote.total ?? 0));

  if (schedule.payment_status === "paid") return "paid";
  if (schedule.payment_status === "pending_verification") return "paid";
  if (schedule.payment_status === "partial_payment") return "partial_payment";
  if (schedule.payment_status === "overdue" || quote.isOverdue) return "overdue";
  if (balance <= 0) return "paid";

  return schedule.payment_status;
}

/** Alineado con computeGlobalStatus del admin: "Al día" → Pagó. */
function computeStudentPaymentStatus(
  schedules: CopyProgramSchedulePaymentStudentType[]
): TeacherPaymentStatus {
  const active = schedules.filter((s) => s.status === "active");
  if (!active.length) return "unpaid";

  if (active.some((s) => resolveSchedulePaymentStatus(s) === "overdue")) {
    return "unpaid";
  }
  if (active.some((s) => resolveSchedulePaymentStatus(s) === "partial_payment")) {
    return "unpaid";
  }

  const hasPendingDebt = active.some((s) => {
    const status = resolveSchedulePaymentStatus(s);
    if (status === "paid") return false;
    const quote = calculateScheduleQuote(s);
    const balance = Math.max(0, Number(s.balance ?? quote.total ?? 0));
    return balance > 0;
  });

  return hasPendingDebt ? "unpaid" : "paid";
}

function extractPaginatedData<T extends object>(
  result: PaginatedResponse<T> | T[]
): T[] {
  if (Array.isArray(result)) return result;
  return result?.data ?? [];
}

export default function useProgramStudentsPaymentSummary(programId: number) {
  const [loading, setLoading] = React.useState(true);
  const [rows, setRows] = React.useState<StudentPaymentSummaryRow[]>([]);

  const fetchData = React.useCallback(async () => {
    if (!programId) {
      setRows([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const studentsRes = await programsStudentAcademy.find({
        query: {
          program_id: programId,
          $limit: 10000,
          $sort: { id: -1 },
        },
      });

      const students = extractPaginatedData(
        studentsRes as PaginatedResponse<ProgramStudentType>
      );

      if (!students.length) {
        setRows([]);
        return;
      }

      const studentIdSet = new Set(students.map((s) => Number(s.id)));

      let schedules: CopyProgramSchedulePaymentStudentType[] = [];

      try {
        // Sin $in en program_student_id: CASL del docente ya filtra por sus programas
        // y Object.assign puede sobrescribir el $in del cliente y devolver 0 filas.
        const schedulesRes = await programPaymentScheduleStudentAcademy.find({
          query: {
            status: "active",
            $limit: 5000,
            $sort: { payment_due_date: 1 },
          },
        });
        schedules = extractPaginatedData(
          schedulesRes as PaginatedResponse<CopyProgramSchedulePaymentStudentType>
        ).filter((schedule) =>
          studentIdSet.has(Number(schedule.program_student_id))
        );
      } catch (scheduleErr) {
        console.warn(
          "No se pudieron cargar las cuotas de pago de los estudiantes:",
          scheduleErr
        );
      }

      const schedulesByStudentId = schedules.reduce(
        (
          acc: Record<number, CopyProgramSchedulePaymentStudentType[]>,
          schedule
        ) => {
          const key = Number(schedule.program_student_id);
          if (!acc[key]) acc[key] = [];
          acc[key].push(schedule);
          return acc;
        },
        {}
      );

      setRows(
        students.map((student) => ({
          ...student,
          paymentStatus: computeStudentPaymentStatus(
            schedulesByStudentId[Number(student.id)] || []
          ),
        }))
      );
    } catch (err) {
      console.error("Error cargando estado de pagos del programa:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [programId]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { loading, rows, refresh: fetchData };
}
